(() => {
  const qs = (s, r) => (r || document).querySelector(s);
  const qsa = (s, r) => Array.from((r || document).querySelectorAll(s));

  // Elements are looked up live (at event time) everywhere below so this
  // script keeps working after the Shopify theme editor replaces the
  // section's DOM on `shopify:section:load` — no rebinding required.
  const FOCUSABLE = 'a[href], button:not([disabled]), input, [tabindex]:not([tabindex="-1"])';

  const isOpen = (el) => !!el && !el.hidden;

  // M0.3: the PDP's sticky add-to-cart bar is `position: fixed; z-index: 40`, well above the
  // nav drawer and the search panel, so on a product page it painted over the bottom of both —
  // it covered a whole menu row and the first search results. The bottom tab bar solves this by
  // sitting at z-index 15, deliberately below every overlay; the sticky bar can't do the same
  // because it has to clear page content. So instead it hides itself while an overlay is open,
  // driven by this flag. Recomputed from live DOM (not incremented) so it can't drift.
  const syncOverlayFlag = () => {
    const anyOpen = isOpen(getDrawer()) || isOpen(getSearchPanel());
    document.documentElement.classList.toggle('bitco-overlay-open', anyOpen);
  };

  const setOverlayState = (el, trigger, open, { lockScroll = false, focusTarget } = {}) => {
    el.hidden = !open;
    if (trigger) trigger.setAttribute('aria-expanded', String(open));
    if (lockScroll) document.documentElement.style.overflow = open ? 'hidden' : '';
    syncOverlayFlag();
    if (open) {
      const target = (focusTarget && qs(focusTarget, el)) || qs(FOCUSABLE, el);
      (target || el).focus();
    } else if (trigger) {
      trigger.focus();
    }
  };

  const getDrawer = () => qs('[data-bitco-drawer]');
  const getDrawerOpenBtn = () => qs('[data-bitco-drawer-open]');
  const getSearchPanel = () => qs('[data-bitco-search-panel]');
  const getSearchOpenBtn = () => qs('[data-bitco-search-open]');
  const getLangWrap = () => qs('[data-bitco-lang]');
  const getLangList = () => qs('.bitco-header__langlist');
  const getLangBtn = () => qs('.bitco-header__langbtn');
  const getMegas = () => qsa('[data-bitco-mega]');

  // M1.10: there are now two [data-bitco-drawer-open] buttons on mobile — the header hamburger
  // and the bottom bar's Shop tab. Remember which one actually opened the drawer so aria-expanded
  // lands on that button and closing returns focus to it, rather than always to whichever comes
  // first in the DOM. Same pattern as searchTrigger below.
  let drawerTrigger = null;

  const openDrawer = (trigger) => {
    const drawer = getDrawer();
    if (!drawer) return;
    drawerTrigger = trigger || getDrawerOpenBtn();
    setOverlayState(drawer, drawerTrigger, true, { lockScroll: true });
  };
  const closeDrawer = () => {
    const drawer = getDrawer();
    if (!isOpen(drawer)) return;
    setOverlayState(drawer, drawerTrigger || getDrawerOpenBtn(), false, { lockScroll: true });
    drawerTrigger = null;
  };

  // There is more than one [data-bitco-search-open] button on mobile — the header's magnifier and
  // the sticky bottom bar's Search tab (snippets/bitco-tabbar.liquid). Remember which one actually
  // opened the panel so aria-expanded is set on that button and closing returns focus to it,
  // rather than always to whichever comes first in the DOM.
  let searchTrigger = null;

  const openSearch = (trigger) => {
    const panel = getSearchPanel();
    if (!panel) return;
    searchTrigger = trigger || getSearchOpenBtn();
    setOverlayState(panel, searchTrigger, true, { focusTarget: 'input[type="search"], input[name="q"]' });
  };
  const closeSearch = () => {
    const panel = getSearchPanel();
    if (!isOpen(panel)) return;
    setOverlayState(panel, searchTrigger || getSearchOpenBtn(), false);
    searchTrigger = null;
  };

  const closeLang = () => {
    const list = getLangList();
    if (!list || list.hidden) return;
    list.hidden = true;
    const btn = getLangBtn();
    if (btn) btn.setAttribute('aria-expanded', 'false');
  };

  const closeMegas = () => {
    getMegas().forEach((m) => { if (m.open) m.open = false; });
  };

  // Announcement rotation ---------------------------------------------
  // Interval id lives on window so it survives this IIFE re-running (it
  // won't, normally, since the section's <script src> isn't re-executed
  // by a plain DOM swap — but this keeps things correct either way) and
  // so a stray previous interval never keeps mutating detached nodes.
  const clearAnnTimer = () => {
    if (window.__bitcoAnnTimer) {
      clearInterval(window.__bitcoAnnTimer);
      window.__bitcoAnnTimer = null;
    }
  };

  const initAnnouncement = () => {
    clearAnnTimer();
    const ann = qs('[data-bitco-announcement]');
    if (!ann) return;
    const items = qsa('.bitco-announcement__item', ann);
    if (!items.length) return;

    items.forEach((it) => it.removeAttribute('data-active'));
    items[0].setAttribute('data-active', '');

    const isMobile = window.matchMedia('(max-width: 749px)').matches;
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (items.length > 1 && isMobile && !reducedMotion) {
      let i = 0;
      window.__bitcoAnnTimer = setInterval(() => {
        items[i].removeAttribute('data-active');
        i = (i + 1) % items.length;
        items[i].setAttribute('data-active', '');
      }, 5000);
    }
  };

  // Document-level delegated click handling -----------------------------
  const onDocumentClick = (e) => {
    const drawerOpenBtn = e.target.closest('[data-bitco-drawer-open]');
    if (drawerOpenBtn) openDrawer(drawerOpenBtn);
    if (e.target.closest('[data-bitco-drawer-close]')) closeDrawer();
    const searchOpenBtn = e.target.closest('[data-bitco-search-open]');
    if (searchOpenBtn) openSearch(searchOpenBtn);
    if (e.target.closest('[data-bitco-search-close]')) closeSearch();

    const langBtn = e.target.closest('.bitco-header__langbtn');
    if (langBtn) {
      const list = getLangList();
      if (list) {
        const open = list.hidden;
        list.hidden = !open;
        langBtn.setAttribute('aria-expanded', String(open));
      }
    }

    // Outside-click closes: lang dropdown + any open mega menu.
    const langWrap = getLangWrap();
    if (langWrap && !langWrap.contains(e.target)) closeLang();
    if (!e.target.closest('[data-bitco-mega]')) closeMegas();
  };

  // Escape (priority order) + Tab focus trap -----------------------------
  const onDocumentKeydown = (e) => {
    if (e.key === 'Escape') {
      if (isOpen(getDrawer())) { closeDrawer(); return; }
      if (isOpen(getSearchPanel())) { closeSearch(); return; }
      const list = getLangList();
      if (list && !list.hidden) { closeLang(); return; }
      if (getMegas().some((m) => m.open)) { closeMegas(); return; }
      return;
    }

    if (e.key === 'Tab') {
      const drawer = getDrawer();
      const panel = getSearchPanel();
      const container = isOpen(drawer) ? drawer : isOpen(panel) ? panel : null;
      if (!container) return;

      const focusables = qsa(FOCUSABLE, container).filter((el) => el.offsetParent !== null);
      if (!focusables.length) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];

      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
  };

  // Mega-menu exclusive open: `toggle` doesn't bubble, so listen on capture.
  const onToggleCapture = (e) => {
    const mega = e.target && e.target.closest && e.target.closest('[data-bitco-mega]');
    if (!mega || !mega.open) return;
    getMegas().forEach((m) => { if (m !== mega) m.open = false; });
  };

  const bindDocumentListenersOnce = () => {
    if (window.__bitcoHeaderBound) return;
    window.__bitcoHeaderBound = true;
    document.addEventListener('click', onDocumentClick);
    document.addEventListener('keydown', onDocumentKeydown);
    // Drawer language/country selects: no submit button, post on change.
    document.addEventListener('change', (e) => {
      const select = e.target.closest('[data-bitco-loc-select]');
      if (select && select.form) select.form.submit();
    });
    document.addEventListener('toggle', onToggleCapture, true);

    // Mega menus open on hover/focus (spec §4); click/Enter keep working via
    // the native <details>. Pointer-only for hover; the existing toggle
    // handler closes siblings whenever one opens.
    let megaHoverTimer = null;
    const hoverCapable = window.matchMedia('(hover: hover)');
    document.addEventListener('pointerover', (e) => {
      if (!hoverCapable.matches) return;
      const mega = e.target.closest('[data-bitco-mega]');
      if (!mega) return;
      clearTimeout(megaHoverTimer);
      if (!mega.open) mega.open = true;
    });
    document.addEventListener('pointerout', (e) => {
      if (!hoverCapable.matches) return;
      const mega = e.target.closest('[data-bitco-mega]');
      if (!mega) return;
      const to = e.relatedTarget;
      if (to && to.closest && to.closest('[data-bitco-mega]') === mega) return;
      clearTimeout(megaHoverTimer);
      megaHoverTimer = setTimeout(() => { mega.open = false; }, 150);
    });
    document.addEventListener('focusin', (e) => {
      const summary = e.target.closest('summary.bitco-header__navlink');
      if (summary) {
        const mega = summary.closest('[data-bitco-mega]');
        if (mega && !mega.open) mega.open = true;
        return;
      }
      const insideMega = e.target.closest('[data-bitco-mega]');
      getMegas().forEach((m) => { if (m.open && m !== insideMega) m.open = false; });
    });
  };

  const bindSectionReloadListenerOnce = () => {
    if (window.__bitcoHeaderSectionListenerBound) return;
    window.__bitcoHeaderSectionListenerBound = true;
    // Section re-render (theme editor) only needs the announcement scan
    // re-run — delegated listeners above already query the live DOM.
    document.addEventListener('shopify:section:load', initAnnouncement);
  };

  // M2.19: the footer's link columns render as <details open> so a no-JS visitor sees the whole
  // footer. Below 750px they collapse — expanded, the footer was ~2100px of links to scroll past.
  // Driven off a matchMedia listener rather than a resize handler so it also fixes itself on an
  // orientation change, and it never fights a shopper who has opened a group by hand: it only
  // writes when the breakpoint itself changes.
  const footerMql = window.matchMedia('(max-width: 749px)');
  const syncFooterGroups = () => {
    qsa('.bitco-footer__group').forEach((group) => {
      group.open = !footerMql.matches;
    });
  };

  const init = () => {
    bindDocumentListenersOnce();
    bindSectionReloadListenerOnce();
    initAnnouncement();
    syncFooterGroups();
    if (!window.__bitcoFooterGroupsBound) {
      window.__bitcoFooterGroupsBound = true;
      footerMql.addEventListener('change', syncFooterGroups);
    }
  };

  document.readyState === 'loading' ? document.addEventListener('DOMContentLoaded', init) : init();
})();
